/**
 * «Аналитика» — по макетам docs/design/analytics-2026-10-08.
 *
 * Шапка та же, что у «Операций»: счёт и период, по умолчанию — от зарплаты
 * до зарплаты. Три раздела:
 *
 * · **Обзор** — картина периода: укладываешься ли, сколько пришло, сколько
 *   ушло на платежи, нужды, свободные траты и накопления, и итоги фактом.
 *   Всегда по всем счетам: план — на все деньги, и сравнивать его с тратами
 *   одной карты нельзя.
 * · **Категории** — куда ушли деньги или откуда пришли, кругом или
 *   столбцами; здесь счёт выбирается.
 * · **Долги** — что должен сейчас.
 *
 * Считает ядро: периоды — `reportPeriods`, план — `periodPlanLines`, факты
 * — `periodFacts`, категории — `rangeReport`. Здесь только показ.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ZERO, activeStablePlan, add, addDays, baselineFits, baselineOf, chartAverage, chartBuckets, chartFacts, clampPos, collapseTail, toRubles,
  conservativeIncome, countsAs, daysIn, diffDays, flowValue, formatMoney, isBefore, isPaused, kop,
  nextByRhythm, periodFacts, rangeReport, rhythmOf, share, sliceOf, spendKind, sub,
  type ChartBucket, type ChartFacts, type ChartFlow, type SpendKind, type CategorySlice, type Money, type PeriodSource, type PlainDate,
  type PlanLineKind, type ReportPeriod,
} from '@skoka/core';
import { Icon } from './icon.js';
import { Tip } from './tip.js';
import { SheetWrap } from './sheet.js';
import { dayMonth, plural } from './text.js';
import { selfPayoutsOf } from './plan-version.js';
import { assignmentsOf, paydayOf, periodBudget, periodsOf } from './period-view.js';
import { PeriodReviewCard } from './period-review-card.js';
import {
  PaydayTip, PeriodHint, PeriodSheet, PeriodTabs, Radio, currentChoice, rangeLabel, rangeOf, rangeText, sharedFilter,
  shortDay, yearsOf, type RangeChoice, type RangeCtx,
} from './period-filter.js';
import { AccountSheet, OpDays, PART_FILTER, PERIOD_HINT, byDay } from './operations.js';
import { CategoryGlyph, colorOf as categoryColor } from './category-icon.js';
import { useAccess } from './access.js';
import { useOpenPro } from './pro-context.js';
import { TabInk } from './tab-ink.js';
import { navigate } from './routes.js';
import { bucketTitle, notesTemplate, sentences, updatedText, useChartNotes } from './chart-notes.js';
import {
  confirmIncomeOccurrence, dismissHint, stableRange, type AppState, type Tx,
} from './store.js';
import { setScreen, track } from './track.js';
import { useSwipeTabs } from './swipe-tabs.js';
import { SWIPE_SECTIONS_HINT, SwipeCoach, coachSeen, coachWanted } from './swipe-coach.js';
import { CategoryField } from './operation-fields.js';

/**
 * Палитра секторов — проверенная (§8.6).
 *
 * Шесть цветов, прошедших шесть проверок в обеих темах. Седьмого цвета нет
 * намеренно — вместо него категории сворачиваются в «Остальное».
 */
const SLICE_COLORS = ['#0F9E72', '#6366E8', '#E8615C', '#2477BE', '#729E0B', '#9160B8'] as const;

type View = 'overview' | 'categories';
/** Раздел живёт, пока открыто приложение. */
const remembered: { view: View; flow: 'expense' | 'income'; part: SpendKind | null; chart: 'pie' | 'bars' } = {
  view: 'overview', flow: 'expense', part: null, chart: 'pie',
};

const cardIds = (state: AppState): string[] =>
  state.accounts.filter((a) => a.kind === 'credit_card').map((a) => a.id);

/*
  «Долги» отсюда ушли в «Деньги → Платежи» (14.10.2026): здесь был тот же
  список кредитов, только беднее, и от выбранного периода он не зависел.
*/
const VIEWS: readonly (readonly [View, string])[] = [['overview', 'Обзор'], ['categories', 'Категории']];
/** Открытый раздел — для отметки экрана в отчёте владельцу. */
export const analyticsView = (): View => remembered.view;

export function Analytics({ state, ask, onMode, onAddIncome, onChanged, onOperations, onEditTx, onDeleteTx }: {
  state: AppState;
  /** «Спроси SKOKA» в шапке экрана. */
  ask?: ReactNode;
  /** Открыть «Операции» — с выбором и поиском из `sharedFilter`. */
  onOperations: () => void;
  /** «Сколько тратить, а сколько копить». */
  onMode: () => void;
  /** Записать приход: из напоминания о задержке зарплаты. */
  onAddIncome: (streamId: string) => void;
  /** Что-то записано — перечитать состояние. */
  onChanged: () => void;
  /** Открыть правку операции — из листа категории. */
  onEditTx: (id: string) => void;
  /** Удалить операцию свайпом — из листа категории. */
  onDeleteTx: (id: string) => void;
}) {
  const today = state.today;
  const ctx: RangeCtx = useMemo(() => {
    const payday = paydayOf(state);
    return { today, periods: periodsOf(state), ...(payday ? { payday } : {}) };
  }, [state, today]);
  const years = useMemo(() => yearsOf(state.transactions.map((t) => t.occurredDate), today), [state, today]);
  const [choice, setChoiceRaw] = useState<RangeChoice>(() => currentChoice(ctx));
  const [accountId, setAccountRaw] = useState<string | null>(
    sharedFilter.accountId && state.accounts.some((a) => a.id === sharedFilter.accountId) ? sharedFilter.accountId : null,
  );
  const [view, setViewRaw] = useState<View>(remembered.view);
  const [sheet, setSheet] = useState<'period' | 'account' | null>(null);
  const setChoice = (c: RangeChoice) => { sharedFilter.range = c; setChoiceRaw(c); };
  const setAccount = (id: string | null) => { sharedFilter.accountId = id; setAccountRaw(id); };
  const setView = (v: View) => { remembered.view = v; setViewRaw(v); };

  const account = accountId ? state.accounts.find((a) => a.id === accountId) : undefined;
  const range = rangeOf(choice, ctx);
  // Разделы листаются свайпом по экрану, как в «Деньгах».
  useSwipeTabs(VIEWS.map(([k]) => k), view, (k) => {
    track('section_switch', { area: 'analytics', to: k, via: 'swipe' });
    setView(k);
  }, { coach: 'sections' });
  useEffect(() => { setScreen(`analytics:${view}`); }, [view]);

  return (
    <div className="ops">
      {/*
        Разделы — заголовок экрана (макет 13.10.2026, вариант Г).

        Прежде заголовком был счёт, а в «Обзоре» он стоял серым и не
        нажимался: «Обзор» всегда по всем счетам. Теперь счёт выбирается там,
        где он что-то меняет, — в фильтрах «Категорий».
      */}
      {coachWanted(state.dismissedHints, SWIPE_SECTIONS_HINT) && sheet === null && (
        <SwipeCoach kind="sections" title="Разделы листаются свайпом"
          text="Проведи пальцем влево или вправо — Обзор и Категории сменяют друг друга. Тянуться к вкладкам наверху не нужно."
          onDone={() => void coachSeen(SWIPE_SECTIONS_HINT)} />
      )}
      <div className="an-headrow">
      <div className="an-sections" role="tablist" aria-label="Разделы аналитики">
        {VIEWS.map(([k, title]) => (
          <button key={k} role="tab" aria-selected={view === k} className={view === k ? 'on' : ''}
            onClick={() => {
              if (k !== view) track('section_switch', { area: 'analytics', to: k, via: 'tap' });
              setView(k);
            }}>{title}</button>
        ))}
        <TabInk row="analytics" active={view} />
      </div>
      {ask}
      </div>

      <PeriodTabs choice={choice} ctx={ctx} years={years} onPick={setChoice} onOpen={() => setSheet('period')} />
      {choice.kind === 'salary' && !state.dismissedHints.includes(PERIOD_HINT) && (
        <PeriodHint ctx={ctx} extra="«Обзор» считается по всем счетам."
          onDismiss={async () => { await dismissHint(PERIOD_HINT); onChanged(); }} />
      )}

      <div className="an-stack">
        {view === 'overview' && (
          <Overview state={state} ctx={ctx} range={range} choice={choice} onMode={onMode} onAddIncome={onAddIncome} onChanged={onChanged}
            onOperations={(key, name) => {
              sharedFilter.range = { kind: 'report', key };
              sharedFilter.query = name;
              onOperations();
            }} />
        )}
        {view === 'categories' && (
          <Categories state={state} range={range} rangeTitle={rangeText(choice, ctx)} rangeName={rangeLabel(choice, ctx)}
            account={account ?? null} onAccount={() => setSheet('account')} onEditTx={onEditTx} onDeleteTx={onDeleteTx} onChanged={onChanged} />
        )}
      </div>

      {sheet === 'period' && (
        <PeriodSheet value={choice} ctx={ctx} years={years}
          context={`${view === 'overview' ? 'Все счета' : account?.name ?? 'Все счета'} · ${rangeText(choice, ctx)}`}
          onClose={() => setSheet(null)}
          onApply={(c) => { setChoice(c); setSheet(null); }} />
      )}
      {sheet === 'account' && (
        <AccountSheet state={state} value={accountId} action="Показать"
          context="Категории считаются по выбранному счёту. «Обзор» — всегда по всем."
          onClose={() => setSheet(null)}
          onApply={(id) => { setAccount(id); setSheet(null); }} />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────── обзор

function Overview({ state, ctx, range, choice, onMode, onAddIncome, onChanged, onOperations }: {
  state: AppState; ctx: RangeCtx; range: ReturnType<typeof rangeOf>; choice: RangeChoice;
  onMode: () => void; onAddIncome: (streamId: string) => void; onChanged: () => void;
  onOperations: (periodKey: string, categoryName: string) => void;
}) {
  const p = range.period;
  // Идущий — открытый: и последний период истории, и календарный месяц.
  const isCurrent = !!p && p.to === null;
  const doubt = p && isCurrent ? doubtOf(state, p) : null;
  return (
    <>
      {/* Спорный приход сначала уточняем: «Записать приход» рядом с ним
          предложил бы записать те же деньги второй раз. */}
      {p && isCurrent && p.overdue && p.kind !== 'month' && awaited(state, p) && !doubt
        && <Overdue period={p} today={state.today} onAdd={() => onAddIncome(p.sourceId ?? '')} />}
      {doubt && <Doubtful state={state} doubt={doubt} onChanged={onChanged} />}
      {p && <PeriodPicture state={state} period={p} isCurrent={isCurrent} onMode={onMode} changed={!!range.changed}
        tip={p.kind === 'salary' && ctx.payday?.periods.some((x) => x.key === p.key) ? <PaydayTip ctx={ctx} /> : null} />}
      {/* Разбор — только у идущего периода: он про прошлый, закрытый. */}
      {isCurrent && <PeriodReviewCard state={state} onChanged={onChanged} onOperations={onOperations}
        periods={ctx.payday && ctx.periods.every((x) => x.kind === 'month') ? ctx.payday.periods : ctx.periods} />}
      {/* Строки плана уже говорят «пришло», «оплачено», «потрачено»,
          «отложено» — таблица ниже повторяла бы их. Остаётся только то,
          что не траты и не доход. */}
      <Totals state={state} from={range.from} to={range.to} title={rangeLabel(choice, ctx)}
        rows={!(p && periodBudget(state, p).known)} />
    </>
  );
}

/**
 * Ждут ли ещё доход периода. Выключенный или удалённый доход, отключённый
 * или приостановленный стабильный бюджет не ждут — и спрашивать «Пришла?» /
 * «Выплатили?» нельзя: человек сам сказал, что этого больше нет. История
 * периодов при этом остаётся.
 */
const awaited = (state: AppState, p: ReportPeriod): boolean => {
  if (p.kind === 'salary') return state.incomeStreams.some((s) => s.id === p.sourceId && s.isActive);
  if (p.kind === 'stable') {
    const plan = activeStablePlan(state.stablePlans);
    return !!plan && plan.id === p.sourceId && !isPaused(plan);
  }
  return true;
};

/** Что начнётся после смены источника — для подписи конца периода. */
const nextLabel = (state: AppState, next: PeriodSource | undefined): string => {
  if (!next || next.kind === 'month') return 'по календарным месяцам';
  if (next.kind === 'stable') return 'от выплаты себе';
  const name = state.incomeStreams.find((s) => s.id === next.sourceId)?.name;
  return name ? `от зарплаты «${name}»` : 'от зарплаты';
};

/** Зарплата не пришла в срок: период тянется, денег в плане не прибавляется. */
function Overdue({ period, today, onAdd }: { period: ReportPeriod; today: PlainDate; onAdd: () => void }) {
  if (!period.plannedTo) return null;
  const due = addDays(period.plannedTo, 1);
  return (
    <section className="an-alert">
      <b>{period.kind === 'stable' ? 'Выплата себе ожидалась' : 'Зарплата ожидалась'} {dayMonth(due, today)}</b>
      <p>{period.kind === 'stable' ? 'Выплатили?' : 'Пришла?'} Пока её нет, период продолжается — но денег в
        плане от этого не прибавляется.</p>
      {period.kind !== 'stable' && (
        <div className="an-btnrow"><button className="btn pri" onClick={onAdd}>Записать приход</button></div>
      )}
    </section>
  );
}

/** Неделя до зарплаты: деньги могли прийти раньше срока. Только повод спросить. */
const EARLY_ASK_DAYS = 7;

/** Спорный приход и два ответа на вопрос о нём. */
interface Doubt {
  tx: Tx;
  /** Зарплата, за которую деньги, возможно, пришли. */
  next: PlainDate;
  /** К какой зарплате они отнесены сейчас. */
  current: PlainDate;
  /** Приход сам открыл период — не доплата, а ранние деньги. */
  early: boolean;
}

/**
 * Спорный приход идущего периода — какую зарплату он закрывает.
 *
 * · Крупная сумма без связи с ожиданием отнесена к текущей зарплате как
 *   доплата — возможно, это следующая раньше срока.
 * · Крупная сумма открыла период за неделю до следующей зарплаты: так
 *   приходит первая зарплата нового человека перед праздниками. Без
 *   вопроса «Аналитика» решила бы, что зарплата не пришла.
 * · То же, но следующая зарплата уже открыла свой период малой частью:
 *   ранние деньги остались в прошлом, а здесь пришло меньше половины.
 *
 * Отнесение по расписанию — догадка; ответ человека (`confirmed`) больше не
 * спрашивается.
 */
function doubtOf(state: AppState, period: ReportPeriod): Doubt | null {
  if (period.kind !== 'salary' || !period.occurrence) return null;
  const stream = state.incomeStreams.find((s) => s.id === period.sourceId);
  if (!stream) return null;
  const half = share(conservativeIncome(stream), 0.5);
  if (half <= ZERO) return null;
  const rhythm = rhythmOf({ ...stream, isActive: true });
  const amountOf = (txId: string) => state.transactions.find((t) => t.id === txId)?.amount ?? ZERO;
  const mine = assignmentsOf(state).filter((a) => a.streamId === stream.id);
  const here = add(...mine.filter((a) => a.occurrence === period.occurrence).map((a) => amountOf(a.txId)));
  for (const a of mine) {
    const tx = state.transactions.find((t) => t.id === a.txId);
    if (a.basis !== 'schedule' || !tx || tx.amount < half) continue;
    const next = nextByRhythm(rhythm, a.occurrence);
    if (!next) continue;
    const soon = diffDays(tx.occurredDate, next) > 0 && diffDays(tx.occurredDate, next) <= EARLY_ASK_DAYS;
    if (a.occurrence === period.occurrence && !a.opens) return { tx, next, current: a.occurrence, early: false };
    if (a.occurrence === period.occurrence && soon) return { tx, next, current: a.occurrence, early: true };
    if (next === period.occurrence && soon && here < half) return { tx, next, current: a.occurrence, early: true };
  }
  return null;
}

/** Вопрос о спорном приходе. Ответ меняет только отнесение операции — не баланс. */
function Doubtful({ state, doubt, onChanged }: { state: AppState; doubt: Doubt; onChanged: () => void }) {
  const { tx, next, current, early } = doubt;
  const answer = async (occurrence: PlainDate) => { await confirmIncomeOccurrence(tx.id, occurrence); onChanged(); };
  if (early) {
    return (
      <section className="an-alert soft">
        <b>{dayMonth(tx.occurredDate, state.today)} записано {formatMoney(tx.amount)}</b>
        <p>Это зарплата за {dayMonth(next, state.today)}?</p>
        <div className="an-btnrow">
          <button className="btn pri" onClick={() => void answer(next)}>Да, зарплата за {dayMonth(next, state.today)}</button>
          <button className="btn" onClick={() => void answer(current)}>Нет, другой приход</button>
        </div>
      </section>
    );
  }
  return (
    <section className="an-alert soft">
      <b>Приход {dayMonth(tx.occurredDate, state.today)} — {formatMoney(tx.amount)}</b>
      <p>Это зарплата за {dayMonth(next, state.today)}? Сейчас он считается доплатой к зарплате
        {' '}{dayMonth(current, state.today)}.</p>
      <div className="an-btnrow">
        <button className="btn pri" onClick={() => void answer(next)}>Да, следующая зарплата</button>
        <button className="btn" onClick={() => void answer(current)}>Нет, это доплата</button>
      </div>
    </section>
  );
}

/** Суммы плана и подписи — в целых рублях: копейки в оценке выглядят точностью, которой нет. */
const whole = (m: Money): string => formatMoney(kop(Math.round(m / 100) * 100));
const pct = (part: Money, of: Money): number => (of > ZERO ? Math.min(100, Math.round((part / of) * 100)) : 0);

const ROWS: { kind: PlanLineKind; label: string; tone: string; icon: string; did: string }[] = [
  { kind: 'income', label: 'Доход', tone: 'inc', icon: 'coins', did: 'пришло' },
  { kind: 'obligations', label: 'Обязательные платежи', tone: 'pay', icon: 'lock', did: 'оплачено' },
  { kind: 'need', label: 'Повседневные нужды', tone: 'need', icon: 'basket-shopping-check', did: 'потрачено' },
  { kind: 'want', label: 'Свободные траты', tone: 'want', icon: 'bag-shopping-heart', did: 'потрачено' },
  { kind: 'goals', label: 'Накопления', tone: 'save', icon: 'piggy-bank', did: 'отложено' },
];

/**
 * Картина периода: то, что было «Планом».
 *
 * Темп сравнивает только нужды и свободные траты: платежи платятся по датам,
 * а не поровну каждый день. Учёт начат посреди периода — главными числами
 * стоит записанное, а оценки нет: ранних трат в приложении нет.
 */
function PeriodPicture({ state, period, isCurrent, onMode, changed, tip }: {
  state: AppState; period: ReportPeriod; isCurrent: boolean; onMode: () => void;
  /** Календарный месяц, в котором сменился доход. */
  changed: boolean;
  /** Пояснение к периоду самой большой зарплаты. */
  tip?: ReactNode;
}) {
  const budget = periodBudget(state, period);
  if (!budget.known) {
    return (
      <section className="blk">
        <div className="blkhead"><b>План периода</b></div>
        <div className="note" style={{ marginTop: 0, paddingBottom: 12 }}>{budget.action}</div>
      </section>
    );
  }
  const { lines, elapsed } = budget.plan;
  const today = state.today;
  // Конец идущего периода: смена источника закончит его не по своему
  // расписанию — тогда прогноз смены, с подписью, что начнётся дальше. Дат
  // у нового источника нет — срок неизвестен, так и говорим.
  const switching = isCurrent && !!period.next;
  const unknownEnd = switching && !period.switchEnd;
  const end = switching ? period.switchEnd ?? null : period.plannedTo;
  const withdrawn = periodFacts({
    txs: state.transactions, from: period.from, to: period.to ?? today, goalEvents: state.goalEvents,
  }).goalsWithdrawn;

  // Срок вышел: доход ждут — тревога; доход выключен — срок плана просто
  // закончился, и период оценивается как прошедший.
  const waiting = period.overdue && awaited(state, period);
  const ended = isCurrent && period.overdue && !waiting;
  const word = switching
    ? (end && isBefore(end, today) ? 'ожидали' : 'ожидаем')
    : waiting ? 'ожидали' : ended ? 'по плану' : 'ожидаем';
  const dates = !isCurrent && period.to
    ? `${shortDay(period.from)} – ${shortDay(period.to)}`
    : `${shortDay(period.from)}${period.start === 'schedule' ? ' по расписанию' : ''}${end
      ? ` – ${word} до ${shortDay(end)}` : unknownEnd ? ' – срок пока неизвестен' : ''}`;
  const day = diffDays(period.from, today) + 1;
  const total = end ? diffDays(period.from, end) + 1 : null;
  // Опоздание — с самого дня прихода: на следующий день — «1 день».
  const late = period.plannedTo ? diffDays(addDays(period.plannedTo, 1), today) : 0;
  const notes = [
    ...(period.partial ? [`данные с ${shortDay(period.observedFrom)}`] : []),
    ...(switching ? [`дальше — ${nextLabel(state, period.next)}`]
      : !period.partial && isCurrent && !ended
        ? [waiting ? `${plural(late, 'день', 'дня', 'дней')} сверх срока` : total ? `${day}-й день из ${total}` : '']
        : []),
  ].filter(Boolean);

  const planned = add(lines.need.planned, lines.want.planned);
  const spent = add(lines.need.actual, lines.want.actual);
  const byToday = share(planned, elapsed);
  const fast = spent > add(byToday, share(planned, 0.05));
  const over = spent > planned;

  const head = (
    <div className="an-top"><b>{isCurrent ? 'Этот период' : 'Период'}{tip ? <> {tip}</> : null}</b>
      <span className="an-dates">{dates}{notes.map((n) => <span key={n}><br />{n}</span>)}</span></div>
  );

  /*
    Только записанное, без оценки выполнения плана:

    · неполный период — учёт начат посреди него, и после закрытия тоже:
      ранние траты от следующей зарплаты известными не стали;
    · период оборвала смена дохода — план считался на другой срок;
    · идущий период, чья основа сохранена до смены дохода, — она считала
      доходы соседнего периода (`baselineFits`); запись основы остаётся.
  */
  const saved = baselineOf(state.periodBaselines, period.key);
  const cut = !isCurrent && !!period.endedBySwitch;
  const unfit = isCurrent && !!saved && !baselineFits(period, saved);
  if (period.partial || cut || unfit || changed) {
    return (
      <>
        <section className="blk pl-state">
          {head}
          {cut && (
            <p className="pl-cap" style={{ marginTop: 10 }}>
              <b>Период завершён из-за смены дохода.</b> План считался на другой срок, поэтому оценки
              нет — только записанное.
            </p>
          )}
          {changed && (
            <p className="pl-cap" style={{ marginTop: 10 }}>
              <b>В этом месяце изменились доходы</b> — показываем записанное.
            </p>
          )}
          {unfit && !changed && (
            <p className="pl-cap" style={{ marginTop: 10 }}>
              <b>Сменился доход:</b> период закончится раньше, чем считал план, поэтому сравнения с
              планом нет — только записанное.
            </p>
          )}
          {period.partial && (
            <p className="pl-cap" style={{ marginTop: 10 }}>
              Учёт начат {dayMonth(period.observedFrom, today)}: что было раньше, в приложении нет. Поэтому
              показываю только записанное{isCurrent
                ? '. «Укладываешься» появится, когда наберётся полный период наблюдений.'
                : ' — оценки, уложился ли ты в план, за этот период нет.'}
            </p>
          )}
        </section>
        <section className="blk">
          <div className="blkhead"><b>{period.partial ? `Записано с ${dayMonth(period.observedFrom, today)}` : 'Записано за период'}</b></div>
          {ROWS.map((r) => (
            <div className="li" key={r.kind}>
              <div className={`pl-ico t-${r.tone}`}><Icon name={r.icon} size={16} /></div>
              <div className="nm"><b>{r.kind === 'income' ? 'Пришло' : r.kind === 'goals' ? 'Отложено' : r.label}</b>
                {/* План — справочно, только когда он считался на этот срок. */}
                {!cut && !unfit && !changed && <span>в плане {whole(lines[r.kind].planned)}</span>}</div>
              <div className="am num">{formatMoney(lines[r.kind].actual)}</div>
            </div>
          ))}
          {isCurrent && (
            <div style={{ padding: '6px 0 12px' }}>
              <button className="btn" onClick={onMode}>Сколько тратить, а сколько копить</button>
            </div>
          )}
        </section>
      </>
    );
  }

  const past = !isCurrent || ended;
  const status: ReactNode = past
    ? (over ? 'Больше плана' : 'В пределах плана')
    : waiting
      ? (period.kind === 'stable' ? 'Срок вышел, выплаты нет' : 'Срок вышел, зарплаты нет')
      : fast ? 'Тратишь быстрее плана' : 'Укладываешься';
  const warn = past ? over : waiting || fast;
  const caption = past
    ? (over ? `Больше плана на ${whole(sub(spent, planned))}.` : `В плане осталось ${whole(sub(planned, spent))}.`)
    : waiting
      ? (over ? `План превышен на ${whole(sub(spent, planned))}.` : `До суммы плана осталось ${whole(sub(planned, spent))}.`)
      : fast
        ? `К этому дню по плану — ${whole(byToday)}, потрачено на ${whole(sub(spent, byToday))} больше.`
        : spent <= byToday
          ? `К этому дню по плану — ${whole(byToday)}, потрачено меньше.`
          : `К этому дню по плану — ${whole(byToday)}, потрачено чуть больше, но пока в пределах плана.`;

  const subOf = (kind: PlanLineKind, did: string): ReactNode => {
    const { planned: pl, actual: a } = lines[kind];
    if (pl === ZERO && a === ZERO) return 'в плане нет';
    const first = <span className="nw">{did} {formatMoney(a)}</span>;
    if (kind === 'income') {
      // «Аналитика» — про план и факты; сколько ещё придёт, говорит главная.
      // В идущем периоде — только пришедшее: недобор преждевременен. Когда
      // итог окончателен — отклонение от плана; правка операций его
      // пересчитывает.
      return past && a < pl ? <>{first} · <span className="nw">меньше плана на {whole(sub(pl, a))}</span></> : first;
    }
    if (kind === 'goals' && withdrawn > ZERO) {
      return <>{first} · <span className="nw">снято {formatMoney(withdrawn)}</span></>;
    }
    if (a > pl && (kind === 'need' || kind === 'want')) {
      return <>{first} · <span className="nw bad">больше плана на {whole(sub(a, pl))}</span></>;
    }
    return <>{first} · <span className="nw">осталось {whole(clampPos(sub(pl, a)))}</span></>;
  };

  return (
    <>
      <section className="blk pl-state">
        {head}
        {planned > ZERO && (
          <>
            <h2 className="pl-h"><i className={`pl-dot${warn ? ' fast' : ''}`} />{status}</h2>
            <div className="pl-of">На нужды и свободные траты потрачено</div>
            <div className="pl-big num">{formatMoney(spent)} <span>из {whole(planned)}</span></div>
            <div className={`pl-track${warn ? ' fast' : ''}`} aria-hidden="true">
              <i style={{ width: `${pct(spent, planned)}%` }} />
              {isCurrent && <b style={{ left: `${Math.round(elapsed * 100)}%` }} />}
            </div>
            <div className="pl-cap">{caption}</div>
          </>
        )}
      </section>
      <section className="blk">
        {ROWS.map((r) => (
          <div className="li" key={r.kind}>
            <div className={`pl-ico t-${r.tone}`}><Icon name={r.icon} size={16} /></div>
            <div className="nm"><b>{r.label}</b>
              <span>{subOf(r.kind, r.did)}</span>
              <div className={`pl-bar t-${r.tone}`}>
                <i style={{ width: `${pct(lines[r.kind].actual, lines[r.kind].planned)}%` }} />
              </div>
            </div>
            <div className="am num">{whole(lines[r.kind].planned)}<small>план</small></div>
          </div>
        ))}
        {isCurrent && (
          <div style={{ padding: '6px 0 12px' }}>
            <button className="btn" onClick={onMode}>Сколько тратить, а сколько копить</button>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Итоги фактом за выбранное время — тем же подсчётом, что и план периода
 * (макет 14.10.2026).
 *
 * Строки — как в блоке плана: значок в цвете части, название, сумма. Когда
 * выбран период с планом, эти суммы уже стоят в его строках, и повторять их
 * не нужно (`rows`). Ниже — «Не траты и не доход»: только то, что не ноль.
 *
 * Здесь не бывает слов «можно потратить»: это отчёт.
 */
function Totals({ state, from, to, title, rows }: {
  state: AppState; from: PlainDate | null; to: PlainDate | null; title: string;
  /** Показать пять строк итогов: у периода без плана их больше негде увидеть. */
  rows: boolean;
}) {
  const f0 = from ?? ('0000-01-01' as PlainDate);
  const t0 = to ?? state.today;
  // Факты — по тому же отрезку, что «Категории» и «Операции»: без границы —
  // без границы. Запас стабильного бюджета — на сегодня: будущего у него нет.
  const facts = periodFacts({
    txs: state.transactions, from: f0, to: to ?? ('9999-12-31' as PlainDate),
    goalEvents: state.goalEvents, selfPayouts: selfPayoutsOf(state), creditCardIds: cardIds(state),
  });
  const stable = from ? stableRange(state, from, addDays(t0, 1)) : null;
  const since = state.trackingStartedAt;
  const partial = since && from && since > from ? since : null;
  const line = (icon: string, tone: string, label: string, value: ReactNode, note?: string) => (
    <div className="li" key={label}>
      <div className={`pl-ico t-${tone}`}><Icon name={icon} size={16} /></div>
      <div className="nm"><b>{label}</b>{note && <span>{note}</span>}</div>
      <div className="am num">{value}</div>
    </div>
  );
  const signed = (m: Money) => (m < ZERO ? `−${formatMoney(sub(ZERO, m))}` : formatMoney(m));
  const other = [
    facts.refunds > ZERO && line('rotate-left', 'inc', 'Возвраты', formatMoney(facts.refunds), 'уже вычтены из трат'),
    facts.goalsWithdrawn > ZERO && line('hand-holding-dollar', 'inc', 'Снято из целей', formatMoney(facts.goalsWithdrawn), 'вернулось в оборот'),
    // Погашение карты — не новая трата: покупки картой уже в расходах.
    facts.toCards > ZERO && line('credit-card', 'inc', 'Внесено на кредитку', formatMoney(facts.toCards), 'покупки картой уже в тратах'),
    facts.fromBuffer > ZERO && line('wallet', 'inc', 'Получено из своего буфера', formatMoney(facts.fromBuffer), 'свои деньги, не доход'),
    stable && stable.reconciled !== ZERO && line('scale-balanced', 'inc', 'Сверка запаса', signed(stable.reconciled), 'разовая поправка запаса, не выплата'),
    stable && line('vault', 'inc', 'Запас стабильного бюджета',
      `${formatMoney(stable.buffer.start)} → ${formatMoney(stable.buffer.end)}`),
    facts.adjustments !== ZERO && line('scale-balanced', 'inc', 'Корректировки остатков', signed(facts.adjustments), 'сверка с реальным остатком'),
  ].filter(Boolean);
  return (
    <>
      {rows && (
        <section className="blk">
          <div className="blkhead"><b>Итоги · {title.toLowerCase()}
            <Tip>{partial && (
              <>Учёт начат {dayMonth(partial, state.today)}: траты и приходы раньше здесь не показаны и
                догадкой не восстанавливаются.{' '}</>
            )}Сколько можно потратить сегодня — на главной, здесь итоги.</Tip></b>
            {partial && <span className="tag soft">данные с {shortDay(partial)}</span>}
          </div>
          {line('coins', 'inc', 'Пришло', formatMoney(facts.income))}
          {line('lock', 'pay', 'Обязательные платежи', formatMoney(facts.obligations))}
          {line('basket-shopping-check', 'need', 'Повседневные нужды', formatMoney(facts.need))}
          {line('bag-shopping-heart', 'want', 'Свободные траты', formatMoney(facts.want))}
          {line('bullseye-arrow', 'save', 'Отложено в цели', formatMoney(facts.goals))}
        </section>
      )}
      {other.length > 0 && (
        <section className="blk">
          <div className="blkhead"><b>Не траты и не доход</b></div>
          {other}
        </section>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────── категории

/** Что открыто в листе: категории строки и отрезок — весь период или выбранный столбец. */
type Opened = { ids: string[]; title: string; from: PlainDate | null; to: PlainDate | null; when?: string };

const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const MONTH_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const cap = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

/** «Суббота, 10 октября» — или «Октябрь» у столбца-месяца. */
function bucketLong(key: string, today: PlainDate): string {
  if (key.length === 7) return cap(bucketTitle(key, today));
  const wd = WEEKDAYS[new Date(`${key}T00:00:00Z`).getUTCDay()] ?? '';
  return `${cap(wd)}, ${dayMonth(key, today)}`;
}

/** Дни столбца, обрезанные периодом графика: месяц не выходит за выбранное время. */
function bucketRange(key: string, from: PlainDate, to: PlainDate): { from: PlainDate; to: PlainDate } {
  if (key.length === 10) return { from: key as PlainDate, to: key as PlainDate };
  const start = `${key}-01` as PlainDate;
  const [y, m] = key.split('-').map(Number);
  const end = new Date(Date.UTC(y!, m!, 0)).toISOString().slice(0, 10) as PlainDate;
  return { from: start < from ? from : start, to: end > to ? to : end };
}

function Categories({ state, range, rangeTitle, rangeName, account, onAccount, onEditTx, onDeleteTx, onChanged }: {
  state: AppState; range: ReturnType<typeof rangeOf>;
  /** «1–13 октября» — для подписи графика. */
  rangeTitle: string;
  /** «Этот месяц» — для подписи листа. */
  rangeName: string;
  account: AppState['accounts'][number] | null;
  onAccount: () => void;
  onEditTx: (id: string) => void;
  onDeleteTx: (id: string) => void;
  /** Категорию поправили прямо из выбора — состояние перечитывается. */
  onChanged: () => void;
}) {
  const accountId = account?.id ?? null;
  const [flow, setFlowRaw] = useState<ChartFlow>(remembered.flow);
  /** Часть плана внутри «Расходов»: нужды, свободные, платежи — как в «Обзоре». */
  const [part, setPartRaw] = useState<SpendKind | null>(remembered.part);
  const [chart, setChartRaw] = useState(remembered.chart);
  const [category, setCategory] = useState<string | null>(null);
  /** Выбранный столбец: ключ дня или месяца. */
  const [pick, setPick] = useState<string | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [showTail, setShowTail] = useState(false);
  const [sheet, setSheet] = useState<'flow' | null>(null);
  const setShow = (f: ChartFlow, k: SpendKind | null) => {
    remembered.flow = f; remembered.part = k; setFlowRaw(f); setPartRaw(k); setCategory(null);
  };
  const partOn = flow === 'expense' ? part : null;
  const setChart = (c: typeof chart) => { remembered.chart = c; setChartRaw(c); };
  // Другой отбор — другой график: выбранный столбец к нему не относится.
  useEffect(() => { setPick(null); }, [range.from, range.to, accountId, flow, partOn, category, chart]);

  // Счёт и часть плана сужают сами операции: график, список, лист и
  // пояснение считаются от одного набора и потому сходятся.
  const txs = useMemo(() => state.transactions.filter((t) => (!accountId
    || t.accountId === accountId || t.counterAccountId === accountId)
    && (!partOn || spendKind(t) === partOn)), [state.transactions, accountId, partOn]);
  const report = useMemo(() => rangeReport(txs, range.from, range.to), [txs, range.from, range.to]);

  const nameOf = (id: string): string => {
    if (id === 'rest') return 'Остальное';
    if (id === 'income-other') return 'Без категории';
    return state.categories.find((c) => c.id === id)?.name ?? 'Без категории';
  };
  // Выбранная категория — её подкатегории; без выбора — все.
  const inside = (id: string): boolean => {
    if (!category) return true;
    let c = state.categories.find((x) => x.id === id);
    while (c) { if (c.id === category) return true; c = state.categories.find((x) => x.id === c!.parentId); }
    return false;
  };
  const all = (flow === 'expense' ? report.byCategory : report.incomeByCategory).filter((x) => inside(x.categoryId));
  // Возвраты больше трат: кольцо минус не нарисует — такие категории отдельной строкой.
  const positive = all.filter((x) => x.sum > ZERO);
  const negative = all.filter((x) => x.sum < ZERO);
  const total = add(...positive.map((x) => x.sum));
  // Итог — с возвратами, один для круга и столбцов: форма графика ответ на
  // «сколько потрачено» не меняет. Доли кольца — от трат, итог — за вычетом
  // возвратов, как в «Операциях».
  const net = add(...all.map((x) => x.sum));
  const { head, tail, rest } = collapseTail(withShares(positive, total), SLICE_COLORS.length);
  const shown = rest ? [...head, rest] : head;
  const headIds = head.map((x) => x.categoryId);
  /** Цвет категории — тот же в кольце, столбцах и списке; хвост — серым, как «Остальное». */
  const colorOf = (id: string): string => {
    const i = headIds.indexOf(id);
    return i >= 0 ? (SLICE_COLORS[i] ?? SLICE_COLORS[0]) : 'var(--ink-3)';
  };
  const groupOf = (id: string): string => (headIds.includes(id) ? id : 'rest');
  const order = [...headIds, 'rest'];

  /** Покупок (поступлений) категорий за отрезок — тот же счёт, что в листе. */
  const countIn = (ids: readonly string[], from: PlainDate | null, to: PlainDate | null): number => txs.filter((t) =>
    (from === null || t.occurredDate >= from) && (to === null || t.occurredDate <= to)
    && countsAs(t, flow) && ids.includes(sliceOf(t, flow))).length;
  const idsOf = (id: string): string[] => (id === 'rest' ? tail.map((x) => x.categoryId) : [id]);

  // Столбцы — до сегодня, а если записано и позже — до последней записи:
  // иначе она была бы в итоге, но не на графике.
  const within = (d: PlainDate) => (range.from === null || d >= range.from) && (range.to === null || d <= range.to);
  const dates = txs.map((t) => t.occurredDate).filter(within).sort();
  const from = range.from ?? (dates[0] as PlainDate | undefined) ?? state.today;
  const lastDate = dates[dates.length - 1];
  const reach = lastDate && lastDate > state.today ? lastDate : state.today;
  const to = range.to && range.to < reach ? range.to : reach;
  const byMonth = diffDays(from, to) > 62;
  const buckets = useMemo(() => chartBuckets({ txs, from, to, flow, byMonth, inside }),
    [txs, from, to, flow, byMonth, category]); // eslint-disable-line react-hooks/exhaustive-deps
  const picked = pick ? buckets.find((b) => b.key === pick) ?? null : null;

  // Что сказать о периоде: те же числа, что на графике и в списке.
  const facts: ChartFacts | null = useMemo(() => {
    const live = range.from !== null && range.from <= state.today && (range.to === null || range.to >= state.today);
    const end = range.period?.plannedTo ?? range.to;
    const want = flow === 'expense' && (!partOn || partOn === 'want') && live && !accountId && !category && end && state.wantBaseline > ZERO
      ? { spent: report.want, usual: state.wantBaseline, daysPassed: daysIn(from, state.today < to ? state.today : to), daysTotal: daysIn(from, end) }
      : undefined;
    return chartFacts({
      flow, from, to, buckets, byMonth, nameOf,
      slices: withShares(positive, total).map((x) => ({ ...x, count: countIn([x.categoryId], range.from, range.to) })),
      ...(want ? { want } : {}),
    });
  }, [buckets, report, flow, partOn, accountId, category]); // eslint-disable-line react-hooks/exhaustive-deps

  const word = (n: number) => (flow === 'expense'
    ? plural(n, 'покупка', 'покупки', 'покупок') : plural(n, 'поступление', 'поступления', 'поступлений'));
  const forText = rangeTitle === 'Всё время' ? 'за всё время' : `за ${rangeTitle}`;

  /** Строка категории: открывает лист её операций — за период или за выбранный столбец. */
  const open = (id: string, span?: { from: PlainDate; to: PlainDate }) => setOpened({
    ids: idsOf(id), title: nameOf(id),
    from: span?.from ?? range.from, to: span?.to ?? range.to,
    ...(span && picked ? { when: bucketLong(picked.key, state.today) } : {}),
  });
  const row = (id: string, sum: Money, sub2: string, color: string | null, span?: { from: PlainDate; to: PlainDate }) => (
    <button key={id} className="li lg w" onClick={() => open(id, span)}>
      {color ? <span className="dot" style={{ background: color }} /> : <span className="dot" style={{ background: 'transparent' }} />}
      <div className="nm"><b>{nameOf(id)}</b><span>{sub2}</span></div>
      <div className={`rt num${sum < ZERO ? ' pos' : ''}`}>{sum < ZERO ? `+${formatMoney(sub(ZERO, sum))}` : formatMoney(sum)}</div>
      <span className="ch"><Icon name="chevron-right" size={13} /></span>
    </button>
  );

  return (
    <>
      <div className="an-filters">
        <button className="an-pick acct" onClick={onAccount} aria-label={`Счёт: ${account?.name ?? 'Все счета'}. Сменить`}>
          <span>{account?.name ?? 'Все счета'}</span> <Icon name="chevron-down" size={12} />
        </button>
        <button className="an-pick flow" onClick={() => setSheet('flow')}>
          {/* Здесь — какие деньги: расходы, платежи, поступления. Нужды и свободные — в выборе категории. */}
          <span>{flow === 'income' ? 'Поступления' : partOn === 'obligations' ? 'Платежи' : 'Расходы'}</span>
          <Icon name="chevron-down" size={12} />
        </button>
        {/* Тот же лист, что при записи операции: группы, дерево, поиск, частые. */}
        <CategoryField categories={state.categories} kind={flow === 'income' ? 'income' : 'expense'}
          value={category ?? ''} onUpdated={() => onChanged()}
          // «Все категории» снимает и отбор по части плана: показываются все расходы.
          onChange={(id) => { if (id) setCategory(id); else setShow(flow, null); }}
          part={partOn === 'need' || partOn === 'want' ? partOn : null}
          onPart={(k) => setShow('expense', k)}
          anyLabel="Все категории"
          otherKindNote={flow === 'income'
            ? 'Чтобы выбрать категорию расхода, переключи «Поступления» на «Расходы».'
            : 'Чтобы выбрать категорию дохода, переключи «Расходы» на «Поступления».'}
          trigger={(open) => (
            <button className="an-pick" onClick={open}>
              <span>{category ? nameOf(category)
                : partOn === 'need' ? 'Повседневные нужды' : partOn === 'want' ? 'Свободные траты' : 'Все категории'}</span>
              <Icon name="chevron-down" size={12} />
            </button>
          )} />
      </div>

      {shown.length === 0 && negative.length === 0 ? (
        <div className="blk"><div className="note" style={{ padding: '14px 2px' }}>
          {flow === 'expense' ? 'Трат за это время нет.' : 'Поступлений за это время нет.'}
        </div></div>
      ) : (
        <>
          <section className="an-chart">
            {picked ? (
              <div className="an-day">
                <div className="when">{bucketLong(picked.key, state.today)}
                  <b className="num"><NetSum net={picked.total} /></b></div>
                <button className="reset" onClick={() => setPick(null)}><Icon name="xmark" size={12} /> Весь период</button>
              </div>
            ) : (
              <div className="an-chart-head">
                <span>{flow === 'income' ? 'Пришло' : net < ZERO ? 'Возвратов больше, чем трат'
                  : partOn ? PART_FILTER.find(([k]) => k === partOn)?.[2] : 'Потрачено'} {forText}</span>
                <div className="an-toggle sm" role="group" aria-label="Вид">
                  <button className={chart === 'pie' ? 'on' : ''} aria-pressed={chart === 'pie'} aria-label="Кругом"
                    onClick={() => setChart('pie')}><Icon name="chart-pie" size={15} /></button>
                  <button className={chart === 'bars' ? 'on' : ''} aria-pressed={chart === 'bars'} aria-label="Столбцами"
                    onClick={() => setChart('bars')}><Icon name="chart-simple" size={15} /></button>
                </div>
              </div>
            )}
            {chart === 'pie'
              ? <Donut shown={shown} total={total} net={net} flow={flow} nameOf={nameOf} colorOf={colorOf} picked={opened && !opened.when ? opened.ids : null} />
              : (
                <>
                  {!picked && <b className="an-chart-sum num"><NetSum net={net} /></b>}
                  <Bars buckets={buckets} byMonth={byMonth} order={order} groupOf={groupOf} colorOf={colorOf}
                    pick={pick} onPick={setPick} today={state.today} flow={flow} />
                </>
              )}
          </section>

          {!picked && <ChartNotes facts={facts} today={state.today}
            view={`${range.from ?? ''}|${range.to ?? ''}|${accountId ?? ''}|${flow}|${partOn ?? ''}|${category ?? ''}`} />}

          {picked ? (() => {
            // Выбранный столбец: его категории, каждая отдельно — их немного.
            const span = bucketRange(picked.key, from, to);
            const items = Object.entries(picked.bySlice).filter(([, v]) => v !== ZERO)
              .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
            const plus = items.filter(([, v]) => v > ZERO);
            const sum = add(...plus.map(([, v]) => v));
            const shares = withShares(plus.map(([categoryId, v]) => ({ categoryId, sum: v, percent: 0 })), sum);
            return (
              <div className="blk">
                <div className="blkhead"><b>{bucketTitle(picked.key, state.today)}</b>
                  <span className="tag soft">{word(picked.count)}</span></div>
                {items.length === 0 && <div className="li"><div className="nm"><span>
                  {flow === 'expense' ? 'В этот день трат нет.' : 'В этот день поступлений нет.'}</span></div></div>}
                {items.map(([id, v]) => {
                  const p = shares.find((x) => x.categoryId === id);
                  return row(id, v, v < ZERO ? 'возвратов больше, чем трат'
                    : `${p?.percent ?? 0} % · ${word(countIn([id], span.from, span.to))}`, v < ZERO ? null : colorOf(id), span);
                })}
              </div>
            );
          })() : (
            <div className="blk">
              {shown.map((x) => row(x.categoryId, x.sum, `${x.percent} % · ${x.categoryId !== 'rest'
                ? word(countIn([x.categoryId], range.from, range.to))
                : plural(tail.length, 'категория', 'категории', 'категорий')}`, colorOf(x.categoryId === 'rest' ? '' : x.categoryId)))}
              {rest && (
                <button className="li lg w" onClick={() => setShowTail(!showTail)}>
                  <div className="nm">{showTail ? 'Свернуть' : `Что внутри «Остального» · ${tail.length}`}</div>
                </button>
              )}
              {rest && showTail && tail.map((x) => row(x.categoryId, x.sum,
                `${x.percent} % · ${word(countIn([x.categoryId], range.from, range.to))}`, null))}
              {negative.map((x) => row(x.categoryId, x.sum, 'возвратов больше, чем трат', null))}
            </div>
          )}
        </>
      )}

      {opened && (
        <CategorySheet state={state} opened={opened} txs={txs} flow={flow} accountId={accountId}
          context={[account?.name ?? 'Все счета', opened.when ?? rangeName,
            flow === 'income' ? 'Поступления' : PART_FILTER.find(([k]) => k === partOn)?.[2] ?? 'Расходы'].join(' · ')}
          onClose={() => setOpened(null)} onEditTx={onEditTx} onDeleteTx={onDeleteTx} />
      )}
      {sheet === 'flow' && (
        <SheetWrap title="Показывать" onClose={() => setSheet(null)} className="ops-sheet">
          <div className="ops-options">
            {[...PART_FILTER.filter(([k]) => k !== 'need' && k !== 'want').map(([k, , full]) => ['expense', k, full] as const),
              ['income', null, 'Поступления'] as const].map(([f, k, title]) => {
              // «Все расходы» — и когда внутри них выбраны нужды или свободные: это отбор категорий.
              const on = flow === f && (f === 'income' || (k === null ? partOn !== 'obligations' : partOn === k));
              return (
                <button key={title} className={`ops-option${on ? ' on' : ''}`} aria-pressed={on}
                  onClick={() => { setShow(f, k); setSheet(null); }}>
                  <span>{title}</span><Radio on={on} />
                </button>
              );
            })}
          </div>
        </SheetWrap>
      )}
    </>
  );
}

/** Кольцо категорий. Открытая в листе категория — толще, остальные приглушены. */
function Donut({ shown, total, net, flow, nameOf, colorOf, picked }: {
  shown: readonly CategorySlice[]; total: Money; net: Money; flow: ChartFlow;
  nameOf: (id: string) => string; colorOf: (id: string) => string; picked: readonly string[] | null;
}) {
  const R = 52;
  const C = 2 * Math.PI * R;
  let acc = 0;
  const on = (id: string) => picked !== null && (picked.includes(id) || (id === 'rest' && picked.length > 1));
  return (
    <div className="donut">
      <svg viewBox="0 0 120 120" role="img"
        aria-label={`${flow === 'expense' ? 'Расходы' : 'Поступления'} по категориям: ${shown.map((x) => `${nameOf(x.categoryId)} ${x.percent}%`).join(', ')}`}>
        <g transform="rotate(-90 60 60)">
          {shown.map((x) => {
            const len = total > 0 ? (C * x.sum) / total : 0;
            const dash = Math.max(0.6, len - (len > 6 ? 2.5 : 0));
            const off = -acc;
            acc += len;
            const dim = picked !== null && !on(x.categoryId);
            return (
              <circle key={x.categoryId} cx="60" cy="60" r={R} fill="none" stroke={colorOf(x.categoryId === 'rest' ? '' : x.categoryId)}
                strokeWidth={on(x.categoryId) ? 18 : 14} strokeOpacity={dim ? 0.28 : 1}
                strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={off} />
            );
          })}
        </g>
      </svg>
      <div className="dctr">
        <div className="dsum"><NetSum net={net} /></div>
        <div className="dlbl">{netLabel(net, flow)}</div>
      </div>
    </div>
  );
}

/**
 * Лист операций категории — с учётом всего выбранного: счёта, периода,
 * «Расходы / Поступления» и столбца. Сумма и число покупок — те же, что у
 * строки, по которой нажали: считаются тем же `flowValue` / `countsAs`.
 */
function CategorySheet({ state, opened, txs, flow, accountId, context, onClose, onEditTx, onDeleteTx }: {
  state: AppState; opened: Opened; txs: readonly Tx[]; flow: ChartFlow; accountId: string | null;
  context: string; onClose: () => void; onEditTx: (id: string) => void; onDeleteTx: (id: string) => void;
}) {
  const list = txs.filter((t) => (opened.from === null || t.occurredDate >= opened.from)
    && (opened.to === null || t.occurredDate <= opened.to)
    && flowValue(t, flow) !== ZERO && opened.ids.includes(sliceOf(t, flow)));
  const sum = add(...list.map((t) => flowValue(t, flow)));
  const n = list.filter((t) => countsAs(t, flow)).length;
  const what = flow === 'expense' ? plural(n, 'покупка', 'покупки', 'покупок') : plural(n, 'поступление', 'поступления', 'поступлений');
  return (
    <SheetWrap title={opened.title} onClose={onClose} className="ops-sheet">
      <p className="ops-context">{context}</p>
      <div className="cat-sum"><span>{what}</span><b className="num"><NetSum net={sum} /></b></div>
      {list.length === 0
        ? <div className="note">{flow === 'expense' ? 'Трат здесь нет.' : 'Поступлений здесь нет.'}</div>
        : <OpDays state={state} days={byDay(list)} accountId={accountId} onOpen={onEditTx} onDelete={onDeleteTx} />}
    </SheetWrap>
  );
}

/**
 * «Что видно за период» под графиком — Pro.
 *
 * Без Pro на этом месте спокойная карточка с той же меткой — без всплывающих
 * окон. Пока доступ не известен или нет сети — ничего: Pro-пользователю
 * «в Pro» показывать нельзя.
 */
function ChartNotes({ facts, view, today }: { facts: ChartFacts | null; view: string; today: PlainDate }) {
  const access = useAccess();
  const openPro = useOpenPro();
  const pro = access?.tier === 'pro' || access?.tier === 'trial';
  const notes = useChartNotes(facts, view, pro);
  if (!facts || !access) return null;
  if (!pro) {
    if (access.offline) return null;
    /*
      Начало настоящего пояснения, остальное размыто (21.10.2026). Текст
      считает само приложение, без модели, — показать его ничего не стоит,
      а «серая карточка с замком» не говорила, что именно человек получит.
    */
    const [first, ...rest] = notesTemplate(facts, today);
    const cut = first ? first.indexOf(' — ') : -1;
    return (
      <section className="an-ai pro-tease">
        <div className="h"><span className="who"><Icon name="sparkles" size={15} /></span>
          <b>Что видно за период<span className="tag pro">Pro</span></b></div>
        {first && <p>{cut > 0 ? <>{first.slice(0, cut)} —</> : first}</p>}
        <p className="tease-blur" aria-hidden="true">{cut > 0 ? first!.slice(cut + 3) : ''} {rest.join(' ')}</p>
        <button className="btn pri" onClick={() => openPro('analytics')}>Открыть пояснение — Pro</button>
      </section>
    );
  }
  const lines = notes.text ? sentences(notes.text) : notesTemplate(facts, today);
  return (
    <section className="an-ai">
      <div className="h"><span className="who"><Icon name="sparkles" size={15} /></span>
        <b>Что видно за период<span className="tag pro">Pro</span></b></div>
      {lines.map((l) => <p key={l}>{l}</p>)}
      <div className="foot">
        {notes.loading ? 'Операции изменились — обновляю пояснение…'
          : `${notes.text ? 'Пишет ИИ по твоим операциям, числа сверены с приложением' : 'Собрано по твоим операциям'}`
            + `${notes.at ? `. Обновлено ${updatedText(notes.at, today)}` : ''}`}
      </div>
    </section>
  );
}

/** Доли от суммы видимых категорий: при выборе категории проценты — внутри неё. */
function withShares(rows: readonly CategorySlice[], total: Money): CategorySlice[] {
  if (total <= ZERO) return rows.map((r) => ({ ...r, percent: 0 }));
  const exact = rows.map((r) => (r.sum * 100) / total);
  const floors = exact.map(Math.floor);
  let left = 100 - floors.reduce((a, b) => a + b, 0);
  const order = exact.map((e, i) => ({ i, frac: e - Math.floor(e) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  const percent = [...floors];
  for (const { i } of order) { if (left <= 0) break; percent[i] = (percent[i] ?? 0) + 1; left -= 1; }
  return rows.map((r, i) => ({ ...r, percent: percent[i] ?? 0 }));
}

/** Итог категорий: возвратов больше трат — плюсом, а не «потрачено −». */
const NetSum = ({ net }: { net: Money }) => <>{net < ZERO ? `+${formatMoney(sub(ZERO, net))}` : formatMoney(net)}</>;
const netLabel = (net: Money, flow: 'expense' | 'income'): string =>
  flow === 'income' ? 'пришло' : net < ZERO ? 'возвратов больше, чем трат' : 'потрачено';

/**
 * Подписи под столбцами: первый, последний и несколько между — не теснее
 * четверти ширины. Выбранный столбец подписан всегда, соседние подписи
 * уступают ему место.
 */
function tickIndexes(n: number, picked: number): number[] {
  if (n <= 7) return [...Array(n).keys()];
  const step = Math.ceil((n - 1) / 4);
  let out: number[] = [];
  for (let i = 0; i < n; i += step) out.push(i);
  if (out[out.length - 1] !== n - 1) {
    if (n - 1 - out[out.length - 1]! < step / 2) out.pop();
    out.push(n - 1);
  }
  if (picked >= 0) out = [...out.filter((i) => Math.abs(i - picked) > 1), picked].sort((a, b) => a - b);
  return out;
}

/**
 * Столбцы по дням (а за длинный период — по месяцам), раскрашенные по
 * категориям теми же цветами, что точки списка. Пунктир — в среднем за
 * столбец. Нажатие выделяет столбец, второе — снимает выделение.
 *
 * Возврат уменьшает свою категорию в свой день; день, где возвратов больше
 * трат, — столбец вниз от нулевой линии. Итог над графиком считается
 * снаружи: высота столбцов арифметику не меняет.
 */
function Bars({ buckets, byMonth, order, groupOf, colorOf, pick, onPick, today, flow }: {
  buckets: readonly ChartBucket[]; byMonth: boolean;
  /** Порядок цветов снизу вверх — как в списке категорий. */
  order: readonly string[];
  groupOf: (categoryId: string) => string;
  colorOf: (group: string) => string;
  pick: string | null; onPick: (key: string | null) => void;
  today: PlainDate; flow: ChartFlow;
}) {
  const cols = buckets.map((b) => {
    const up = new Map<string, Money>();
    let down = ZERO;
    for (const [id, v] of Object.entries(b.bySlice)) {
      if (v > ZERO) { const g = groupOf(id); up.set(g, add(up.get(g) ?? ZERO, v)); } else down = add(down, sub(ZERO, v));
    }
    return { b, up, sum: add(...up.values()), down };
  });
  const maxUp = Math.max(0, ...cols.map((c) => c.sum));
  const maxDown = Math.max(0, ...cols.map((c) => c.down));
  const scale = Math.max(1, maxUp + maxDown);
  // Нулевая линия — от низа, на высоте самого глубокого возврата.
  const zero = (maxDown / scale) * 100;
  const avg = chartAverage(buckets);
  const n = buckets.length;
  const pickedAt = pick ? buckets.findIndex((b) => b.key === pick) : -1;
  const nowKey = byMonth ? today.slice(0, 7) : today;
  const label = (key: string) => (byMonth ? MONTH_SHORT[Number(key.slice(5, 7)) - 1] ?? key : String(Number(key.slice(8, 10))));
  const per = byMonth ? 'в месяц' : 'в день';
  return (
    <div className="an-bars">
      <div className={`an-cols${pick ? ' picking' : ''}`}>
        {maxDown > 0 && <b className="an-zero" style={{ bottom: `${zero}%` }} />}
        {cols.map((c) => (
          <button key={c.b.key} type="button" className={`an-col${pick === c.b.key ? ' on' : ''}${c.sum === 0 && c.down === 0 ? ' nil' : ''}`}
            aria-pressed={pick === c.b.key}
            aria-label={`${bucketTitle(c.b.key, today)}: ${c.b.total < ZERO ? `возвратов больше на ${formatMoney(sub(ZERO, c.b.total))}` : formatMoney(c.b.total)}`}
            onClick={() => onPick(pick === c.b.key ? null : c.b.key)}>
            {c.sum > ZERO && (
              <span className="up" style={{ bottom: `${zero}%`, height: `${(c.sum / scale) * 100}%` }}>
                {order.filter((g) => (c.up.get(g) ?? ZERO) > ZERO).map((g) => (
                  <i key={g} style={{ height: `${((c.up.get(g) ?? ZERO) / c.sum) * 100}%`, background: colorOf(g) }} />
                ))}
              </span>
            )}
            {c.down > ZERO && <span className="dn" style={{ top: `${100 - zero}%`, height: `${(c.down / scale) * 100}%` }} />}
          </button>
        ))}
        {avg > ZERO && <i className="an-avg" style={{ bottom: `${zero + (avg / scale) * 100}%` }} />}
      </div>
      <div className="an-ticks" aria-hidden="true">
        {tickIndexes(n, pickedAt).map((i) => {
          const key = buckets[i]!.key;
          return (
            <span key={key} className={key === pick || key === nowKey ? 'now' : ''} style={{ left: `${((i + 0.5) / n) * 100}%` }}>
              {label(key)}
            </span>
          );
        })}
      </div>
      {avg > ZERO && <div className="an-legend"><i />в среднем {formatMoney(toRubles(avg))} {per}</div>}
      {!pick && (
        <div className="an-bars-hint">
          Цвет — категория из списка ниже. Нажми на столбец — покажу {byMonth ? 'этот месяц' : 'этот день'}.
        </div>
      )}
      {maxDown > 0 && (
        <div className="an-bars-hint">
          Ниже линии — {byMonth ? 'месяцы' : 'дни'}, когда {flow === 'expense' ? 'возвратов было больше, чем трат' : 'вернули больше'}.
        </div>
      )}
    </div>
  );
}

